import React from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { useSelector } from 'react-redux';
import { NewHireProps } from '../../../../models/arc-epi-props';
import { CoverageFormModel, CoveragePlans, DeclinedValue, NewHireCoverageState, PlanTypes } from '../models/coverage-models';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { TcsmStates } from '../../../../../../root/tcsm-model';
import { WhoWillEnrollCriterias, WhoWillEnrollOptions } from '../../models/who-will-enroll-models';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { getLetterMonthDate } from '../../../../../../global/helpers/date-helpers';
import { LabelAndValue } from '../../../../../../global/components/data-display/label-value';
import { BillingAccountOptionValues } from '../../../new-participant-enrollment/models/form-options';
import { CalculateVolumeRate } from '../../models/new-hire-enrollment-models';
import { getCurrencyFormat } from '../../../../../../global/helpers/format-helpers';
import { isInstitutionOfficerOnly } from '../../../../../../global/helpers/auth-helpers';
import { WysiwygWithReactLink } from '../../../../../../global/components/wysiwyg-with-react-link';




interface Props {
	settings: NewHireProps;
	formValues: CoverageFormModel;
	coverageState: NewHireCoverageState;
	editLink: JSX.Element;
	isdioApprover?: boolean;
}

export function CoveragePlansReview(props: Props) {

	const { settings, formValues, coverageState, editLink, isdioApprover } = props;

	const whoWillEnroll = useSelector<TcsmStates, WhoWillEnrollCriterias>(state => state?.newHireEnrollmentState.whoWillEnrollCriteria);
	const groupLifeValuesArray = useSelector<TcsmStates, CalculateVolumeRate[]>(state => state?.newHireEnrollmentState?.calculatedVolumeRateValues);
	const institutionOfficerOnly = isInstitutionOfficerOnly();
	let glRateFromApi: any = 0;
	let glVolumeFromApi: any = 0;

	if (!!groupLifeValuesArray && (Object.keys(groupLifeValuesArray).length !== 0)) {
		glRateFromApi = groupLifeValuesArray[`volume${formValues.G}`]?.rate;
		glVolumeFromApi = groupLifeValuesArray[`volume${formValues.G}`]?.volume;
	}


	function RenderInstitutionAdminWarningMessage() {
		if (institutionOfficerOnly) {
			return (
				<ContentSpacer>
					<Banner
						banner={{
							type: 'warning',
							label: <WysiwygWithReactLink content={settings?.institutionAdminPlanSelectionWarningMessage} />,
						}}
					/>
				</ContentSpacer>
			);

		}
		return null;
	}
	function RenderPlanDetail(
		plans: CoveragePlans,
		planType: PlanTypes,
		declineMessage: string,
		planTitle: string,
		whoWillEnrollOption?: boolean,
		pushToMemberCheckMessage?: string,
		pushToMemberUnCheckMessage?: string,
		rateOverride?: number,
		volumeOverride?: number,
		isDeclined?: boolean,
	) {
		//Declining Plan
		if (!plans?.plans || plans?.plans?.length === 0) {
			return null;
		}

		if (whoWillEnrollOption) {
			return (
				<DividerSection sectionText={planTitle} secondarySectionNode={editLink} >

					<p>{formValues[`${planType}_member`] ? pushToMemberCheckMessage : pushToMemberUnCheckMessage}</p>

				</DividerSection>
			);
		}

		if (isDeclined) {
			return (
				<DividerSection sectionText={planTitle} secondarySectionNode={editLink} >
					{RenderInstitutionAdminWarningMessage()}
					<p>{declineMessage}</p>
				</DividerSection>
			);
		}

		const matchedPlan = plans?.plans?.find(p => p?.code === formValues[planType]);

		return (
			<DividerSection sectionText={planTitle} secondarySectionNode={editLink} >
				{
					RenderInstitutionAdminWarningMessage()
				}
				{
					!formValues[planType] ?
						declineMessage :
						<ContentSpacer>
							<h4>{matchedPlan?.name}</h4>
						</ContentSpacer>
				}
				{
					formValues[planType] &&

					<ContentSpacer>
						<LabelAndValue
							label={settings?.billingAccountLabel}
							value={formValues[`${planType}_billingAccountType`] === BillingAccountOptionValues.INSTITUTION ? plans?.account_number : plans?.diocese_account_number}
						/>
						{(matchedPlan?.volume || volumeOverride) && <LabelAndValue label={settings?.volumeLabel} value={getCurrencyFormat(volumeOverride || matchedPlan?.volume)} />}
						{(matchedPlan?.rate || rateOverride) && < LabelAndValue label={settings?.rateLabel} value={getCurrencyFormat(rateOverride || matchedPlan?.rate)} />}
						{plans?.effective_date && < LabelAndValue label={settings?.effectiveDateLabel} value={getLetterMonthDate(plans?.effective_date)} />}

					</ContentSpacer>
				}
			</DividerSection>
		);
	}


	return (
		<>
			{

				RenderPlanDetail(
					coverageState?.eligible_plans?.group_life,
					PlanTypes.G,
					settings?.declineGroupCoverageMessage,
					settings?.groupLifePlanTitle,
					null,
					null,
					null,
					glRateFromApi,
					glVolumeFromApi ?? formValues[`volume${formValues.G}`],
					formValues[PlanTypes.G] === DeclinedValue,
				)
			}
			{
				RenderPlanDetail(
					coverageState?.eligible_plans?.short_term_disability,
					PlanTypes.W,
					settings?.declineStdCoverageMessage,
					settings?.shortDisPlanTitle,
					isdioApprover ? coverageState?.eligible_plans?.short_term_disability?.member_selection_required : (whoWillEnroll?.disability === WhoWillEnrollOptions.member || whoWillEnroll?.std === WhoWillEnrollOptions.member),
					settings?.stdPlanToMemberReviewMessage,
					settings?.stdPlanToMemberDeclinedReviewMessage,
					null,
					null,
					formValues[PlanTypes.W] === DeclinedValue,
				)
			}
			{
				RenderPlanDetail(
					coverageState?.eligible_plans?.long_term_disability,
					PlanTypes.L,
					settings?.declineLtdCoverageMessage,
					settings?.longDisPlanTitle,
					isdioApprover ? coverageState?.eligible_plans?.long_term_disability?.member_selection_required : whoWillEnroll?.ltd === WhoWillEnrollOptions.member,
					settings?.ltdPlanToMemberReviewMessage,
					settings?.ltdPlanToMemberDeclinedReviewMessage,
					null,
					null,
					formValues[PlanTypes.L] === DeclinedValue,
				)
			}
			{
				RenderPlanDetail(
					coverageState?.eligible_plans?.uei,
					PlanTypes.U,
					settings?.declineUeiCoverageMessage,
					settings?.ueiPlanTitle,
					isdioApprover ? coverageState?.eligible_plans?.uei?.member_selection_required : whoWillEnroll?.disability === WhoWillEnrollOptions.member,
					settings?.ueiPlanToMemberReviewMessage,
					settings?.ueiPlanToMemberDeclinedReviewMessage,
					null,
					null,
					formValues[PlanTypes.U] === DeclinedValue,
				)
			}
		</>
	);
}

CoveragePlansReview.defaultProps = {
	isdioApprover: false,
};